import { Button } from "@/components/ui/baseUi/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/baseUi/dialog"
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/baseUi/input-group"
import { errorToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { useUser } from "@/modules/users/hooks/useUser"
import SessionEmptySVG from "@images/empty/session_empty.svg"
import { CheckmarkIcon } from "@images/svgIcons/checkmarkIcon"
import { word } from "@modules/core/utils/i18n"
import { ExploreFilter } from "@modules/explore/exploreTypes"
import { useExploreSearchContents } from "@modules/explore/hooks/useExplore"
import useDebounce from "@modules/hooks/useDebounce"
import { ProfileAvatar } from "@modules/profile/common/components/profileAvatar"
import { UserEntity } from "@modules/profile/userProfile/userEntity"
import { LoaderCircle, Search, X } from "lucide-react"
import { useCallback, useImperativeHandle, useMemo, useState } from "react"

export type SearchMultipleUserInputModalRef = {
  open: () => void
}

type SearchMultipleUserInputModalProps = {
  title: string
  description?: string
  emptyDescription?: string
  searchPlaceholder?: string
  values: string[]
  onConfirm: (values: UserEntity[]) => Promise<void>
  ref: React.Ref<SearchMultipleUserInputModalRef>
}

export function SearchMultipleUserInputModal({
  title,
  description,
  emptyDescription,
  searchPlaceholder,
  values,
  onConfirm,
  ref,
}: SearchMultipleUserInputModalProps) {
  const user = useUser()
  const [showModal, setShowModal] = useState(false)
  const [selectedMembers, setSelectedMembers] = useState<UserEntity[]>([])
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false)
  const [searchTerms, setSearchTerms] = useState<string>("")
  const debouncedSearchTerm = useDebounce(searchTerms, 300)

  const {
    data: { content: result },
    isLoading,
  } = useExploreSearchContents(ExploreFilter.PROFILE, debouncedSearchTerm)

  const confirm = async () => {
    setIsSubmitting(true)
    try {
      await onConfirm(selectedMembers)
      onHide()
    } catch (e) {
      toast.error(word("global.error.label"), {
        description: e instanceof Error ? e.message : "",
        classNames: errorToastClassName(),
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const filteredResult = useMemo(() => {
    if (!searchTerms) return []

    return result
      .filter(
        (content) =>
          content.profile &&
          !values.includes(content.profile.id) &&
          user?.id !== content.profile.id &&
          !selectedMembers.some((member) => member.id === content.profile?.id),
      )
      .map((content) => content.profile as UserEntity)
  }, [values, result, searchTerms, user?.id, selectedMembers])

  const onHide = useCallback(() => {
    setSearchTerms("")
    setShowModal(false)
  }, [])

  const handleMemberClick = (member: UserEntity) => {
    setSelectedMembers((prev) => [...prev, member])
    setSearchTerms("")
  }

  const handleSelectedMemberClick = (member: UserEntity) => {
    setSelectedMembers((prev) => prev.filter((p) => p.id !== member.id))
  }

  useImperativeHandle(ref, () => ({
    open: () => {
      setShowModal(true)
      setSelectedMembers([])
    },
  }))

  return (
    <Dialog open={showModal} onOpenChange={onHide}>
      <DialogContent className="w-2xl">
        <DialogHeader>
          <DialogTitle>
            <div className="flex shrink flex-col gap-4">
              <div className="flex flex-col flex-wrap items-center justify-center gap-2 text-center">
                <Text fontStyle="TL">{title}</Text>
                {description && (
                  <Text fontStyle="BM" className="text-textDefault-caption">
                    {description}
                  </Text>
                )}
              </div>
              <InputGroup>
                <InputGroupInput
                  placeholder={searchPlaceholder}
                  onChange={(e) => setSearchTerms(e.currentTarget.value)}
                  value={searchTerms}
                />
                <InputGroupAddon align="inline-start">
                  <Search />
                </InputGroupAddon>
                {searchTerms && (
                  <InputGroupAddon align="inline-end">
                    <X className="size-4 cursor-pointer" onClick={() => setSearchTerms("")} />
                  </InputGroupAddon>
                )}
              </InputGroup>
            </div>
          </DialogTitle>
        </DialogHeader>
        <div className="grow">
          <Text fontStyle="LS" className="mt-2 mb-3 flex text-textDefault-body uppercase">
            {selectedMembers.length > 0 && searchTerms.length === 0
              ? word("boxSessionAddSubscriberModal.dialog.selectedTerm")
              : word("boxSessionAddSubscriberModal.dialog.searchTerm")}
          </Text>

          {selectedMembers.length === 0 && searchTerms.length === 0 ? (
            emptyTemplate(emptyDescription)
          ) : searchTerms.length > 0 ? (
            /* IF filteredResult is empty : do empty screen*/
            isLoading ? (
              <div className="flex h-77.5 items-center justify-center">
                <LoaderCircle className="animate-spin" />
              </div>
            ) : filteredResult.length === 0 ? (
              noResult()
            ) : (
              <div className="flex h-77.5 flex-col gap-2 overflow-y-auto">
                {filteredResult.map((member) => (
                  <ItemTemplate
                    key={"item-member-" + member.id}
                    member={member}
                    onClick={() => handleMemberClick(member)}
                  />
                ))}
              </div>
            )
          ) : (
            <div>
              <div className="flex h-77.5 flex-col gap-2 overflow-y-auto">
                {selectedMembers.map((member) => (
                  <SelectedTemplate
                    key={"selected-member-" + member.id}
                    member={member}
                    onClick={() => handleSelectedMemberClick(member)}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <div className="mt-4 flex w-full items-end gap-3">
            <Button variant="outline" onClick={() => onHide()} className="flex-1" size="lg">
              {word("global.cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={() => {
                void confirm()
              }}
              className="flex-1"
              size="lg"
              isLoading={isSubmitting}
              disabled={isSubmitting || selectedMembers.length === 0}
            >
              {isSubmitting && <LoaderCircle className="animate-spin" />}
              {word("boxSessionAddSubscriberModal.dialog.button.addToList")}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

const noResult = () => {
  return (
    <div className="flex h-77.5 flex-col justify-center">
      <Text fontStyle="LL" className="flex justify-center text-center text-textDefault-body">
        {word("boxSessionAddSubscriberModal.dialog.noResult")}
      </Text>
      <div className="mt-4 flex items-center justify-center text-center">
        <img src={SessionEmptySVG} alt="SessionEmptySVG" />
      </div>
    </div>
  )
}

const emptyTemplate = (emptyDescription?: string) => {
  return (
    <div className="flex h-77.5 flex-col justify-center">
      <Text fontStyle="LL" className="flex justify-center text-center text-textDefault-body">
        {emptyDescription ?? word("boxSessionAddSubscriberModal.dialog.empytemplate.body")}
      </Text>
      <div className="mt-4 flex items-center justify-center text-center">
        <img src={SessionEmptySVG} alt="SessionEmptySVG" />
      </div>
    </div>
  )
}

type ItemTemplateProps = {
  member: UserEntity
  onClick: (member: UserEntity) => void
  className?: string
} & React.ComponentProps<"div">

function ItemTemplate({ member, onClick, className, ...props }: ItemTemplateProps) {
  return (
    <div
      className={cn(
        "flex w-full cursor-pointer border-b border-neutral-200 py-3 first:pt-0 last:border-b-0",
        className,
      )}
      onClick={() => onClick(member)}
      key={member.id}
      {...props}
    >
      <ProfileAvatar profile={{ avatar: member.avatar, username: member.firstname }} className="size-15" />
      <div className="ml-2 flex flex-col flex-wrap justify-center gap-1">
        <Text fontStyle="LL">{`${member.firstname} ${member.lastname}`}</Text>
        <Text fontStyle="BS" className="text-textDefault-caption">
          {`${member.username}`}
        </Text>
      </div>
    </div>
  )
}

type SelectedTemplateProps = {
  member: UserEntity
  onClick: (member: UserEntity) => void
  className?: string
} & React.ComponentProps<"div">

function SelectedTemplate({ member, onClick, className, ...props }: SelectedTemplateProps) {
  return (
    <div
      className={cn(
        "flex w-full cursor-pointer items-center justify-center rounded-full bg-neutral-100 py-2 pr-4",
        className,
      )}
      onClick={() => onClick(member)}
      key={member.id}
      {...props}
    >
      <div className="ml-2 flex grow">
        <ProfileAvatar profile={{ avatar: member.avatar, username: member.firstname }} className="size-15" />
        <div className="ml-2 flex flex-col flex-wrap justify-center gap-1">
          <Text fontStyle="LL">{`${member.firstname} ${member.lastname}`}</Text>
          <Text fontStyle="BS" className="text-textDefault-caption">
            {`${member.username}`}
          </Text>
        </div>
      </div>
      <div className="mr-3 flex items-center">
        <CheckmarkIcon
          className="text-textDefault-body"
          color="currentColor"
          style={{
            width: "24px",
            height: "24px",
          }}
        />
      </div>
    </div>
  )
}
